// src/core/math.ts — Skaler yardımcılar + deterministik gürültü. three'siz, tahsissiz.
// Sim'de Math.random / Date.now YASAK: tüm rastgelelik hashNoise(seed, tick, id) ile üretilir (ARCHITECTURE değişmez 3).
import { TICK_HZ } from '../config/constants.ts';

export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
export const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
/** Hermite smoothstep: e0 altı 0, e1 üstü 1 (yürüme hız eğrisi 0.18 → 1.0, ölü bölge). */
export function smoothstep(e0: number, e1: number, x: number): number {
  const t = clamp01((x - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
}
/** Kare hızından bağımsız üssel yaklaşma: cur → target, oran λ (1/s). Kamera yumuşatması (λ 8 / 3 / 6) bunu kullanır. */
export function expDecay(cur: number, target: number, lambda: number, dt: number): number {
  return target + (cur - target) * Math.exp(-lambda * dt);
}
/** expDecay ile aynı; "damp" adı viewer/kamera kodundan gelen alışkanlık için. */
export const damp = expDecay;
/** Bir tick'lik üssel sürtünme çarpanı: τ saniyelik zaman sabiti → her tick ×exp(−DT/τ) (yere kayma τ 0.36 s → ×0.955). */
export const tickDecay = (tau: number): number => Math.exp(-1 / (TICK_HZ * tau));

export const secToTicks = (s: number): number => Math.round(s * TICK_HZ);
export const ticksToSec = (t: number): number => t / TICK_HZ;
export const degToRad = (d: number): number => d * (Math.PI / 180);
export const radToDeg = (r: number): number => r * (180 / Math.PI);

/** 32-bit karıştırıcı (lowbias32, Ellis) — tam sayı girdiden iyi dağılmış tam sayı. */
function mix32(x: number): number {
  x = Math.imul(x ^ (x >>> 16), 0x7feb352d);
  x = Math.imul(x ^ (x >>> 15), 0x846ca68b);
  return (x ^ (x >>> 16)) >>> 0;
}
/**
 * Deterministik gürültü: aynı (seed, tick, id) → aynı [0, 1) değeri; bitişik tick/id'ler bağımsız görünür.
 * id: dövüşçü/bot id'si ya da "tuz" (aynı tick'te birden çok karar için farklı tuzlar kullanılır).
 * Tahsissiz, çağrı başına ~10 tam sayı işlemi.
 */
export function hashNoise(seed: number, tick: number, id: number): number {
  let h = mix32((seed | 0) ^ 0x9e3779b9);
  h = mix32(h ^ Math.imul(tick | 0, 0x85ebca6b));
  h = mix32(h ^ Math.imul((id | 0) + 0x27d4eb2f, 0xc2b2ae35));
  return h / 4294967296;   // [0, 1)
}
/** hashNoise'ın [−1, 1) sürümü (DI, nişan hatası, itişme yönü). */
export const hashNoiseSigned = (seed: number, tick: number, id: number): number => hashNoise(seed, tick, id) * 2 - 1;
/** Yumuşak 1B gürültü (kamera sarsıntısı): tam sayı düğümler arasında smoothstep; t sürekli. */
export function smoothNoise(seed: number, t: number, id: number): number {
  const i = Math.floor(t), f = t - i;
  const a = hashNoiseSigned(seed, i, id), b = hashNoiseSigned(seed, i + 1, id);
  const u = f * f * (3 - 2 * f);
  return a + (b - a) * u;
}
